// @ts-strict-ignore
import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useContext } from 'react';
import { UserContext } from '~/dashboard/context';
import log from '~/helpers/logging';
import type { Cells, CustomerData, CustomerInput } from '../types';
import { ResourceType } from '../types';
import { modalErrorMap } from './constants';
import { fetchAwsToken, getCurrentCell } from './fetchTokens';
import { formatCustomerData } from './formatData';

type MutationReturnType = {
  cells: Cells;
  customers: CustomerData[];
};

type MutationOptions = Omit<
UseMutationOptions<MutationReturnType, unknown, CustomerInput>,
'mutationKey' | 'mutationFn'
>;

/**
 * Creates a customer item in specified cell and Provisioning's dynamodb.
 *
 * Vanity domains are created for customers in UK or Australia.
 */
export const useCreateCustomerMutation = (options?: MutationOptions) => {
  const {
    agent, alloyEnvironment, provisioningLambdaUrl, awsGlobalServicesRegion,
  } = useContext(UserContext);

  return useMutation({
    mutationKey: [
      provisioningLambdaUrl,
      ResourceType.CREATE_CUSTOMER,
      { email: agent.email },
    ],
    mutationFn: async customerInput => {
      if (!provisioningLambdaUrl) {
        log('error', 'Invalid provisioning URL');
        throw new Error('Invalid or Undefined AWS token exception');
      }

      const awsToken = await fetchAwsToken({
        payload: { customer: customerInput },
        email: agent?.email,
        provisioningLambdaUrl,
        resource: ResourceType.CREATE_CUSTOMER,
        isAfefParentAgentRequest: agent?.isAfefParentAgent,
        alloyEnvironment,
        awsRegion: awsGlobalServicesRegion,
      });

      if (!awsToken) {
        log('error', 'Undefined AWS token exception');
        throw new Error('Invalid or Undefined AWS token exception');
      }

      const response = await fetch(provisioningLambdaUrl, {
        method: 'POST',
        headers: awsToken,
        body: JSON.stringify({
          resource: ResourceType.CREATE_CUSTOMER,
          email: agent?.email,
          isAfefParentAgentRequest: agent?.isAfefParentAgent || undefined,
          requestCellKey: getCurrentCell(alloyEnvironment),
          customer: customerInput,
        }),
      });

      const jsonResp = await response.json();

      /**
       * expect 201 but also checking 200 for local
       * ongoing bug where localstack always returns 200
       * https://github.com/localstack/localstack/issues/8213
       *  */
      if (response.status !== 201 && response.status !== 200) {
        const message = 'Error creating provisioning customer exception';
        const error = new Error(message);
        log(
          'error',
          message,
          { status: response.status },
          error,
        );
        throw new Error(modalErrorMap[response.status ?? 500]);
      }

      const customers = jsonResp.data.customers?.length
        ? jsonResp.data.customers
        : [];

      const cells = jsonResp.data.cells ?? {};

      return {
        cells,
        customers: formatCustomerData(customers, cells),
      };
    },
    ...options,
  });
};
